import moment, { Moment } from "moment-timezone";

import { IanaTimezoneName, TimePeriod } from "api/generated/model";
import { toEndDate, toStartDate } from "components/dateFilter/configuration";

export interface DateRange {
    start: Moment;
    end: Moment;
}

interface DateRangeOptions {
    timezone: IanaTimezoneName;
    weekStartsOn: "locale" | "monday";
}

/**
 * Resolved in `timezone`, so the range covers the same days for every user
 * with that profile timezone, whatever their browser's zone.
 * `null` means an incomplete `Custom` range — callers skip the fetch.
 */
export function getDateRange(
    period: TimePeriod,
    { timezone, weekStartsOn }: DateRangeOptions
): DateRange | null {
    if (period.type === "Custom") {
        const { startDate, endDate } = period.data;
        if (!startDate || !endDate) {
            return null;
        }
        return {
            start: moment.tz(startDate, timezone),
            end: moment.tz(endDate, timezone),
        };
    }

    const [unit, offset] = PRESET_UNITS[period.type];
    const anchor = moment.tz(timezone).add(offset, unit);
    const calendarUnit =
        unit === "week" && weekStartsOn === "monday" ? "isoWeek" : unit;

    return {
        start: anchor.clone().startOf(calendarUnit),
        end: anchor.clone().endOf(calendarUnit),
    };
}

/** Every preset is the current or previous whole week/month/quarter/year. */
const PRESET_UNITS: Record<
    Exclude<TimePeriod["type"], "Custom">,
    [moment.unitOfTime.DurationConstructor, number]
> = {
    CurrentWeek: ["week", 0],
    PreviousWeek: ["week", -1],
    CurrentMonth: ["month", 0],
    PreviousMonth: ["month", -1],
    CurrentQuarter: ["quarter", 0],
    PreviousQuarter: ["quarter", -1],
    CurrentYear: ["year", 0],
    PreviousYear: ["year", -1],
};

export function formattedDateRange({ start, end }: DateRange): {
    startDate: string;
    endDate: string;
} {
    const startDate = toStartDate(start).format("YYYY-MM-DDTHH:mm:ssZ");
    const endDate = toEndDate(end).format("YYYY-MM-DDTHH:mm:ssZ");
    return { startDate, endDate };
}
